import { useState } from "react";
import { useAuth } from "../../../context/AuthContext";
import { useNavigate, useParams } from "react-router-dom";
import { IoIosArrowRoundBack } from "react-icons/io";
import QuestionSection from "../../ui/QuestionSection/QuestionSection";
import CommonFooter from "../../CommonFooter/CommonFooter";
import CommonButton from "../../ui/CommonButton/CommonButton";
import classes from "./ExaminationProcesses.module.css";
import { useAccreditation } from "../../../hooks/useAccreditation";

const ExaminationProcesses = () => {
  const { labId } = useParams();
  const { user, token } = useAuth();
  const navigate = useNavigate();

  const [currentSet, setCurrentSet] = useState(0);
  const [isEditing, setIsEditing] = useState(false);

  const {
    formData,
    setFormData,
    loading,
    hydrated,
    fetching,
    editable,
    setEditable,
    handleDone,
  } = useAccreditation({
    labId,
    section: "Examination Processes",
    name: "SADCAS F134-B",
    auditorId: user._id,
    isAssessor: user.role === "ASSESSOR",
    isEditing,
    token,
  });

  const handleAnswerChange = (
    questionId,
    score,
    comment,
    points,
    content
  ) => {
    setFormData((prev) => ({
      ...prev,
      [questionId]: { score, comment, points, content },
    }));
  };

  const questionSets = [
    {
      questions1: [
        {
          questionId: "Q1",
          content:
            "a. Was the examination procedure documented to the extent necessary to ensure the consistent application of activities and the validity of results?",
          number: "7.3.6 (a)",
          multiline: true,
          placeholder: "Add comment",
          hideOptions: true,
        },
        {
          questionId: "Q2",
          content:
            "b. Were documented procedures written in a language understood by the laboratory personnel and available in appropriate locations?",
          number: "7.3.6 (b)",
          multiline: true,
          placeholder: "Add comment",
          hideOptions: true,
        },
        {
          questionId: "Q3",
          content:
            "c. Is the examination procedure validated for its intended use?",
          number: "7.3.1 (a)",
          multiline: true,
          placeholder: "Add comment",
          hideOptions: true,
        },
        {
          questionId: "Q4",
          content:
            "d. If yes, did the laboratory conduct a verification to ensure that the required performance as specified by the manufacturer or method can be achieved?",
          number: "7.3.2",
          multiline: true,
          placeholder: "Add comment",
          hideOptions: true,
        },
        {
          questionId: "Q5",
          content: "e. Was the verification procedure available?",
          number: "",
          multiline: true,
          placeholder: "Add comment",
          hideOptions: true,
        },
        {
          questionId: "Q6",
          content:
            "f. Were the performance specifications for the examination method confirmed during the verification relevant to the intended use of the examination results?",
          number: "7.3.2 (b)",
          multiline: true,
          placeholder: "Add comment",
          hideOptions: true,
        },
        {
          questionId: "Q7",
          content:
            "g. Was the extent of the verification of examination methods sufficient to ensure the validity of results pertinent to clinical decision making?",
          number: "7.3.2 (c)",
          multiline: true,
          placeholder: "Add comment",
          hideOptions: true,
        },
        {
          questionId: "Q8",
          content:
            "h. Were the verification results reviewed by authorised personnel and was the review recorded indicating whether the results meet the specified requirements?",
          number: "7.3.2 (d)",
          multiline: true,
          placeholder: "Add comment",
          hideOptions: true,
        },
        {
          questionId: "Q9",
          content:
            "i. If the examination procedure is a non-standard method / laboratory designed or modified method, is it validated?",
          number: "7.3.3",
          multiline: true,
          placeholder: "Add comment",
          hideOptions: true,
        },
        {
          questionId: "Q10",
          content:
            "j. Were the specific validation requirements for the intended use of the examination fulfilled?",
          number: "7.3.3 (b)",
          multiline: true,
          placeholder: "Add comment",
          hideOptions: true,
        },
        {
          questionId: "Q11",
          content:
            "k. Were the validation results reviewed and recorded?",
          number: "7.3.3 (c)",
          multiline: true,
          placeholder: "Add comment",
          hideOptions: true,
        },
        {
          questionId: "Q12",
          content:
            "l. Was measurement uncertainty evaluated and maintained?",
          number: "7.3.4 (a)",
          multiline: true,
          placeholder: "Add comment",
          hideOptions: true,
        },
        {
          questionId: "Q13",
          content:
            "m. Was MU compared against performance specifications?",
          number: "7.3.4 (a)",
          multiline: true,
          placeholder: "Add comment",
          hideOptions: true,
        },
        {
          questionId: "Q14",
          content:
            "n. What is the frequency of MU evaluation review?",
          number: "7.3.4 (b)",
          multiline: true,
          placeholder: "Add comment",
          hideOptions: true,
        },
        {
          questionId: "Q15",
          content:
            "o. Were reference intervals and clinical decision values defined and communicated?",
          number: "7.3.5",
          multiline: true,
          placeholder: "Add comment",
          hideOptions: true,
        },
      ],
      questions2: [
        {
          questionId: "Comment1",
          multiline: true,
          placeholder: "Add comment",
          hideOptions: true,
        },
      ],
      render: (onAnswerChange, answers) => (
        <>
          <QuestionSection
            topQuestion={false}
            accreditation
            addWidth
            showPointsField={false}
            questions={questionSets[0].questions1}
            onAnswerChange={editable ? onAnswerChange : () => {}}
            answers={answers}
            disabled={!editable}
          />
          <QuestionSection
            topQuestion={false}
            showQuestion={false}
            showEmptyNumber={false}
            accreditation
            showPointsField={false}
            questions={questionSets[0].questions2}
            onAnswerChange={editable ? onAnswerChange : () => {}}
            answers={answers}
            disabled={!editable}
          />
        </>
      ),
    },
  ];

  const handleNext = () => {
    if (currentSet < questionSets.length - 1) {
      setCurrentSet((prev) => prev + 1);
    }
  };

  const handlePrevious = () => {
    if (currentSet > 0) {
      setCurrentSet((prev) => prev - 1);
    }
  };

  if (!hydrated || fetching) {
    return <div className={classes.loading}>Loading...</div>;
  }

  return (
    <div className={`${classes.wrapper} ${!editable ? classes.fromParams : ""}`}>
      <div className="d-flex justify-content-between align-items-center">
        <div
          className="d-flex align-items-center gap-2 mb-4"
          onClick={() => navigate(-1)}
          role="button"
        >
          <IoIosArrowRoundBack size={35} />
          <h2 className="m-0">Clause 7.3: Examination Processes</h2>
        </div>

        {user.role === "ASSESSOR" && !editable && (
          <CommonButton
            className={classes.editButton}
            onClick={() => {
              setIsEditing(true);
              setEditable(true);
            }}
            title="Edit"
          />
        )}
      </div>

      {!editable ? (
        <div className={classes.infoText}>
          ✅ Your SADCAS B - Clause 7.3: Examination Processes has been submitted.
        </div>
      ) : (
        questionSets[currentSet].render(handleAnswerChange, formData)
      )}

      {editable && (
        <CommonFooter
          handlePrevious={handlePrevious}
          handleNext={handleNext}
          handleDone={handleDone}
          cancelText="Cancel"
          handleCancel={() => {
            setEditable(false);
          }}
          currentSet={currentSet}
          questionSets={questionSets}
          doneText={loading ? "Submitting..." : "Update"}
          disabled={loading}
        />
      )}
    </div>
  );
};

export default ExaminationProcesses;